// Globals
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

// Store
import { useAppDispatch } from '@/store';
import { createBlogPost, deleteBlogPost, getBlogPosts, updateBlogPost } from '../../feature';

// Components
import { Modal, ModalSize } from '@/components';
import { PlusIcon, TrashIcon } from '@/icons';
import { Button, Pagination, Table } from '@/ui';
import BlogPostForm from '../../components/BlogPostForm/BlogPostForm';
import { Search } from '../../components/Search';
import { StyledButtons, StyledContainer, StyledMessage } from './BlogContainer.styles';

// Models
import { IBlogPost } from '@/models/blogPost.model';

// Modules
import { Header } from '@/modules/Layout';

// Helpers
import CreateBlogPostDTO from '../../dtos/CreateBlogPosts.dto';
import { GetBlogPostsDTO } from '../../dtos/GetBlogPosts.dto';
import UpdateBlogPostDTO from '../../dtos/UpdateBlogPosts.dto';
import { blogPostsColumns } from '../../helpers/blogPostColumns';
import {
  BlogPostFormFields,
  BlogPostFormType,
  TCreateBlogPostFormValues,
  TUpdateBlogPostFormValues,
} from '../../helpers/types';
import { useBlogPosts } from '../../hooks/useBlogPosts';

export const BlogContainer: FC = () => {
  const { t } = useTranslation(['common']);
  const { blogPosts, isLoading, currentChannel, pagination } = useBlogPosts();
  const [query] = useSearchParams();
  const dispatch = useAppDispatch();

  const [selectedBlogPost, setSelectedBlogPost] = useState<IBlogPost | null>(null);
  const [formType, setFormType] = useState<BlogPostFormType | null>(null);

  const handleDelete = (record: IBlogPost) => {
    setSelectedBlogPost(record);
    setFormType(BlogPostFormType.DELETE);
  };

  const handleOpenEdit = (record: IBlogPost) => {
    setSelectedBlogPost(record);
    setFormType(BlogPostFormType.UPDATE);
  };

  const handlePageChange = (page: number) => {
    if (!currentChannel?.slug) return;
    const dto = new GetBlogPostsDTO({
      page,
      query: query.get('search') || undefined,
      channelSlug: currentChannel.slug,
    });
    dispatch(getBlogPosts(dto));
  };

  const handleOpenCreate = () => {
    setSelectedBlogPost(null);
    setFormType(BlogPostFormType.CREATE);
  };

  const handleCloseModal = () => {
    setFormType(null);
  };

  const handleSubmitUpdateBlogPost = useCallback(
    async (values: TUpdateBlogPostFormValues) => {
      if (!selectedBlogPost) return;
      const updateDto = new UpdateBlogPostDTO({
        ...values,
        slug: selectedBlogPost?.slug,
        blogPostFiles: [],
      });

      try {
        await dispatch(updateBlogPost(updateDto)).unwrap();
        setSelectedBlogPost(null);
        setFormType(null);
      } catch {}
    },
    [dispatch, selectedBlogPost],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedBlogPost) return;
    try {
      await dispatch(deleteBlogPost({ slug: selectedBlogPost.slug })).unwrap();
    } catch (error) {}

    setSelectedBlogPost(null);
    setFormType(null);
  }, [dispatch, selectedBlogPost]);

  const handleSubmitCreateBlogPost = useCallback(
    async (values: TCreateBlogPostFormValues) => {
      setSelectedBlogPost(null);
      if (!currentChannel?.slug) return;
      const createDto = new CreateBlogPostDTO({ ...values, blogPostFiles: [] });
      const getVariablesDto = new GetBlogPostsDTO({
        page: query.get('page') || 1,
        query: query.get('search') || undefined,
        channelSlug: currentChannel.slug,
      });
      try {
        await dispatch(createBlogPost(createDto)).unwrap();
        if (query.get('page') !== '1') {
          dispatch(getBlogPosts(getVariablesDto)).unwrap();
        }
        setFormType(null);
      } catch {}
    },
    [currentChannel?.slug, dispatch, query],
  );

  const getModalContent = useCallback(
    (type: BlogPostFormType) => {
      switch (type) {
        case BlogPostFormType.UPDATE:
          return (
            <BlogPostForm
              onSubmit={handleSubmitUpdateBlogPost}
              isLoading={isLoading}
              formType={BlogPostFormType.UPDATE}
              initialValues={{
                [BlogPostFormFields.SLUG]: selectedBlogPost?.slug,
                [BlogPostFormFields.CONTENT]: selectedBlogPost?.content,
                [BlogPostFormFields.TITLE]: selectedBlogPost?.title,
                [BlogPostFormFields.SHORT_DESCRIPTION]: selectedBlogPost?.shortDescription,
                [BlogPostFormFields.STATUS]: selectedBlogPost?.status,
                [BlogPostFormFields.SEO]: selectedBlogPost?.seo,
                [BlogPostFormFields.PUBLISHED_AT]: dayjs(selectedBlogPost?.publishedAt),
                [BlogPostFormFields.IMAGE_TITLE]: selectedBlogPost?.imageTitle,
                [BlogPostFormFields.BLOG_POST_CHANNELS]: selectedBlogPost?.blogPostChannels?.map(
                  (blogPostChannel) => blogPostChannel.channelId,
                ),
                [BlogPostFormFields.BANNER]: selectedBlogPost?.blogPostFiles?.find((file) => file.metaType === 'banner')
                  ?.file?.url,
                [BlogPostFormFields.SIMILAR_BLOG_POSTS]: selectedBlogPost?.similarBlogPosts?.map((blogPost) => ({
                  value: blogPost.id,
                  label: blogPost.title,
                })),
              }}
            />
          );
        case BlogPostFormType.DELETE:
          return (
            <div>
              <StyledMessage>{t(`delete_blog_post_message`)}</StyledMessage>
              <StyledButtons>
                <Button
                  type="primary"
                  danger
                  size="small"
                  onClick={handleConfirmDelete}
                  icon={<TrashIcon />}
                  loading={isLoading}
                >
                  {t('delete_blog_post')}
                </Button>
              </StyledButtons>
            </div>
          );
        case BlogPostFormType.CREATE:
          return (
            <BlogPostForm
              formType={BlogPostFormType.CREATE}
              isLoading={isLoading}
              onSubmit={handleSubmitCreateBlogPost}
            />
          );
        default:
          return null;
      }
    },
    [handleConfirmDelete, handleSubmitCreateBlogPost, handleSubmitUpdateBlogPost, isLoading, selectedBlogPost, t],
  );

  const title = useMemo(() => {
    switch (formType) {
      case BlogPostFormType.UPDATE:
        return t('title_edit_blog_post');
      case BlogPostFormType.DELETE:
        return t('delete_blog_post_confirm', { code: selectedBlogPost?.slug });
      case BlogPostFormType.CREATE:
        return t('title_create_blog_post');
      default:
        return null;
    }
  }, [formType, selectedBlogPost?.slug, t]);

  return (
    <>
      <Header title={t('menu_blog')}>
        <Button icon={<PlusIcon />} type="primary" onClick={handleOpenCreate}>
          {t('btn_add_blog_post')}
        </Button>
      </Header>
      <StyledContainer>
        <Search loading={isLoading} />
        <Table
          columns={blogPostsColumns(t, handleDelete, handleOpenEdit)}
          rowKey="id"
          dataSource={blogPosts ?? []}
          loading={isLoading}
          pagination={false}
          size="small"
          rowHoverable
          virtual
        />
        <Pagination
          disabled={isLoading}
          total={pagination?.total ?? 0}
          current={pagination?.page ?? 1}
          pageSize={pagination?.pageSize ?? 10}
          onChange={handlePageChange}
          hideOnSinglePage
          align="center"
        />
      </StyledContainer>
      <Modal
        title={title}
        size={formType === BlogPostFormType.DELETE ? ModalSize.MEDIUM : ModalSize.LARGE}
        visible={!!formType}
        onCancel={handleCloseModal}
        destroyOnClose
      >
        {!!formType && getModalContent(formType)}
      </Modal>
    </>
  );
};
